/*
 * Copyright (c) 2024, ResMed Inc. All rights reserved.
 */
import { PlatformLocation } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { environment } from 'src/app/environment/environment';
import { patient_search_payload } from 'src/app/interfaces/patient_search_interfaces';
import { SharedService } from 'src/app/services/shared.service';
import { queryErrShow, switchboardCall } from 'src/app/utils/common_functions';

@Component({
  selector: 'app-patient-search',
  templateUrl: './patient-search.component.html',
  styleUrl: './patient-search.component.scss',
})
export class PatientSearchComponent {
  response: any = '';
  queryError: boolean = false;
  errorMsg: string = '';
  patientId: string = '';
  triggerPatientId: string = '';
  private subscriptions: Subscription[] = [];

  results: any;
  displaySearch: boolean = false;

  successfulTrigger: boolean = false;
  mquiClose: boolean = false;

  mquiDisplay: boolean = false;
  showTooltip = false;

  constructor(
    private elementRef: ElementRef,
    private httpClient: HttpClient,
    private cdr: ChangeDetectorRef,
    private platformLocation: PlatformLocation,
    private sharedService: SharedService
  ) {}

  ngOnInit(): void {
    // Subscribe to successfulTrigger
    const successfulTriggerSub =
      this.sharedService.successfulTrigger$.subscribe((value) => {
        this.successfulTrigger = value;
        if (value) {
          this.search();
          this.sharedService.setSuccessfulTrigger(false);
        }
      });
    this.subscriptions.push(successfulTriggerSub);

    // Subscribe to mquiClose
    const mquiCloseSub = this.sharedService.mquiClose$.subscribe((value) => {
      this.mquiClose = value;
      if (value) {
        this.search();
        this.sharedService.setMquiClose(false);
      }
    });
    this.subscriptions.push(mquiCloseSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  async search(): Promise<void> {
    this.mquiDisplay = false;
    const results = this.elementRef.nativeElement.querySelector(
      '#results'
    ) as HTMLDivElement;

    if (this.patientId === '') {
      console.log('No Patient ID set');
      this.queryError = true;
      this.errorMsg = 'No Patient ID set';
      results.classList.add('hidden');
      this.cdr.detectChanges();
      queryErrShow(this.elementRef);
      this.queryError = false;
      return;
    }

    console.log('Search fired off with values: ', this.patientId);
    await this.executeQuery();

    //Check if query returned responses
    if (
      this.queryError === false &&
      this.response.patient_profile.Count === 0
    ) {
      console.log('No values found');
      this.queryError = true;
      this.errorMsg = 'Query returned no results, check console logs';
      results.classList.add('hidden');
    }

    if (this.queryError) {
      //Force detect changes so updated error msg is loaded
      this.cdr.detectChanges();
      queryErrShow(this.elementRef);
      this.queryError = false;
      return;
    }

    if (this.response.mqui_url && this.response?.schedules.length > 0) {
      this.mquiDisplay = true;
    }

    //Set all display flags to true to display results and force detect changes to load in
    this.displaySearch = true;
    results.classList.remove('hidden');
    this.cdr.detectChanges();
  }

  async executeQuery(): Promise<void> {
    const patient_search_payload: patient_search_payload = {
      patientId: this.patientId,
    };

    try {
      this.response = await switchboardCall(
        {
          data: patient_search_payload,
          action: 'patient_profile_search',
          mqui_domain: environment.mquiDomain,
        },
        environment.switchboardUri,
        this.platformLocation,
        this.httpClient
      );
    } catch (error) {
      console.error(error);
      this.errorMsg =
        `An error occurred: ` + (error as Error).message + ` Check logs`;
      this.queryError = true;
    }
  }

  regexExtract(regexString: string, input: string): string {
    const regex: RegExp = new RegExp(regexString, 'g');
    const match: RegExpExecArray | null = regex.exec(input);
    return match ? match[1] : '';
  }

  isArray(data: any): boolean {
    return Array.isArray(data);
  }

  parseEscapedJson(jsonString: string): any {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.error('Failed to parse JSON:', e);
      return {};
    }
  }

  getParsedInput(schedule: any): any {
    const inputJson = schedule?.Target?.Input;
    return this.parseEscapedJson(inputJson);
  }

  openFilledModal(
    selectedSurveyId: string,
    patientId: string,
    dayOffset: string,
    deviceSerial: string
  ): void {
    let modalData = {
      selectedSurveyId: selectedSurveyId,
      patientId: patientId,
      dayOffset: dayOffset,
    } as any;
    if (deviceSerial) {
      modalData.deviceSerial = deviceSerial;
    }
    this.sharedService.triggerModal(modalData);
  }

  launchMqui() {
    const data = {
      mquiUrl: this.response.mqui_url,
      surveyId: this.response.invite_data.surveyId,
    };
    this.sharedService.mquiModal(data);
  }
}
